<template>
  <BittsCallout
    v-if="isIntegrationActive && hasMissingPopulationSelection"
    class="mt-16"
    type="warning"
    title="Your integration is active, but no populations are selected."
    :subtitle="alertDescription"
  />
  <IntegrationSettingsOverlapEstimates
    v-if="settingType !== 'crossbeam_copilot' && settingType !== 'webhook'"
    :populations="populationSettings?.population_settings"
    :standard-populations="populationSettings?.standard_population_settings"
  />
  <IntegrationSettingsOwnPopulations
    class="mt-16"
    :integration-profile-settings="populationSettings"
    :is-disabled
    :setting-type="settingType"
    @update:settings="onSettingsUpdatedMine"
  />
  <slot name="net-new-accounts" />
  <IntegrationSettingsPartnerSpecificPopulations
    class="mt-16"
    :integration-profile-settings="populationSettings"
    :is-disabled
    :setting-type="settingType"
    :hide-offline-partners="hideOfflinePartners"
    @update:partner-default-settings="onPartnerStandardSettingsUpdated"
    @update:partner-overrides="onPartnerSpecificSettingsUpdated"
    @update:exclude-partnerships-created-since="
      onUpdateExcludePartnershipsCreatedSince
    "
  />
</template>

<script setup lang="ts">
import { BittsCallout } from '@crossbeam/bitts';

import { computed } from 'vue';

import IntegrationSettingsOverlapEstimates from '@/components/integrations/IntegrationSettingsOverlapEstimates.vue';

import useDatabricks from '@/composables/useDatabricks';
import { useConnectionsStore, usePartnerCloudStore } from '@/stores';
import {
  IntegrationProfileType,
  MinimalIntegrationProfile,
} from '@/types/integration_profiles';

import IntegrationSettingsOwnPopulations from './IntegrationSettingsOwnPopulations.vue';
import IntegrationSettingsPartnerSpecificPopulations from './IntegrationSettingsPartnerSpecificPopulations.vue';

const props = withDefaults(
  defineProps<{
    populationSettings?: MinimalIntegrationProfile | null;
    isDisabled?: boolean;
    settingType: IntegrationProfileType;
    hideOfflinePartners?: boolean;
  }>(),
  {
    populationSettings: undefined,
    isDisabled: false,
    hideOfflinePartners: false,
  },
);

const emit = defineEmits<{
  (e: 'save:changes', payload: MinimalIntegrationProfile): void;
  (e: 'update:exclude-partnerships-created-since', isChecked: boolean): void;
}>();

const alertDescription = computed(() =>
  props.settingType === 'crossbeam_copilot'
    ? 'Select at least one of your populations and one partner population to start surfacing overlap data'
    : 'Select at least one of your populations and one partner population to start pushing data',
);

const TRAY_TYPE_BY_PROFILE = {
  salesforce: 'tray_salesforce_push',
  hubspot: 'tray_hubspot',
  microsoft_dynamics: 'tray_microsoft_dynamics',
  clari: 'tray_clari',
} as const;

const connectionsStore = useConnectionsStore();
const partnerCloudStore = usePartnerCloudStore();
const { isPushEnabled: isDatabricksPushEnabled } = useDatabricks();

const isIntegrationActive = computed(() => {
  switch (props.settingType) {
    case 'crossbeam_copilot':
      return true;
    case 'webhook':
      return false;
    case 'snowflake': {
      const connection = connectionsStore.getConnectionByType('snowflake');
      return !!connection && !connection.is_paused;
    }
    case 'databricks':
      return isDatabricksPushEnabled.value;
    case 'salesforce':
    case 'hubspot':
    case 'microsoft_dynamics':
    case 'clari': {
      const trayType = TRAY_TYPE_BY_PROFILE[props.settingType];
      return partnerCloudStore.enabledTray.some((t) => t.type === trayType);
    }
    default:
      return false;
  }
});

const hasMissingPopulationSelection = computed(() => {
  if (!props.populationSettings) return false;

  const { population_settings, standard_population_settings } =
    props.populationSettings;

  const hasOwnPopulation = population_settings.some(
    (pop) => pop.is_included && !pop.is_partner_population,
  );
  const hasPartnerPopulation =
    population_settings.some(
      (pop) => pop.is_included && pop.is_partner_population,
    ) || standard_population_settings.some((pop) => pop.is_included);

  return !hasOwnPopulation || !hasPartnerPopulation;
});

function onSettingsUpdatedMine(payload: MinimalIntegrationProfile) {
  emit('save:changes', {
    ...props.populationSettings,
    population_settings: [
      ...payload.population_settings,
      ...(props.populationSettings?.population_settings.filter(
        (pop) => pop.is_partner_population,
      ) ?? []),
    ],
    standard_population_settings:
      props.populationSettings?.standard_population_settings ?? [],
  });
}
function onPartnerStandardSettingsUpdated(payload: MinimalIntegrationProfile) {
  emit('save:changes', {
    ...props.populationSettings,
    population_settings: props.populationSettings?.population_settings ?? [],
    standard_population_settings: payload.standard_population_settings,
  });
}
function onPartnerSpecificSettingsUpdated(payload: MinimalIntegrationProfile) {
  emit('save:changes', {
    ...props.populationSettings,
    population_settings: payload.population_settings,
    standard_population_settings:
      props.populationSettings?.standard_population_settings ?? [],
  });
}
function onUpdateExcludePartnershipsCreatedSince(isChecked: boolean) {
  emit('update:exclude-partnerships-created-since', isChecked);
}
</script>
